import { Component, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { GoogleMapsModule } from '@angular/google-maps';
import { OrderService } from '../transaction/order/order.service';
import { SellerOrderSummaryDTO, OrderStatus, TrustRelationshipDTO, TrustClient, UserProfileClient, UserProfileDTO, UserInfoDTO } from 'src/app/base/models/models.swagger.generated';
import { DateTime } from 'luxon';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/base/services/auth.service';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';

interface MarkerInfo {
  position: google.maps.LatLngLiteral;
  title: string;
  label?: string;
  icon?: google.maps.Icon | google.maps.Symbol;
  orderDate?: DateTime;
  orderCount?: number;
  isPendingOrder?: boolean;
  orderId?: number;
  userId?: string;
  isUserLocation?: boolean;
  zIndex?: number;
}

interface TrustConnectionCard {
  id: number;
  userId: string;
  userName: string;
  userAddress: string;
  userPhotoBase64?: string;
  latitude?: number;
  longitude?: number;
  connectedDate: DateTime;
  isRecent: boolean;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ChartModule,
    GoogleMapsModule,
    TranslateModule,
    DropdownModule,
    FormsModule,
    ButtonModule,
    AvatarModule,
    DividerModule,
    SkeletonModule
  ],
  providers: [TrustClient, UserProfileClient],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  pendingOrdersCount = 0;
  totalOrdersCount = 0;
  totalTrustedConnections = 0;
  newTrustedConnections = 0;
  chartData: any;
  chartOptions: any;
  mapCenter: google.maps.LatLngLiteral = { lat: 12.9716, lng: 77.5946 };
  mapZoom = 12;
  markers: MarkerInfo[] = [];
  mapFilterOptions = [
    { label: 'All Orders', value: 'ALL' },
    { label: 'Pending Orders', value: 'PENDING' },
    { label: 'Recent Orders (Last 7 Days)', value: 'RECENT_7' },
    { label: 'My Location', value: 'MY_LOCATION' }
  ];

  selectedMapFilter: string | { label: string; value: string } = 'MY_LOCATION';
  allMarkers: MarkerInfo[] = [];
  orderMarkers: MarkerInfo[] = [];
  userMarker: MarkerInfo | null = null;
  currentUser: UserProfileDTO | null = null;

  
  trustConnections: TrustConnectionCard[] = [];
  isLoadingTrustConnections = false;

  private readonly MARKER_COLORS = {
    RECENT: '#EF5350',
    MODERATE: '#42A5F5',
    OLD: '#9E9E9E',
    PENDING: '#FFA726',
    USER_LOCATION: '#4CAF50'
  };

  constructor(
    private orderService: OrderService,
    private trustClient: TrustClient,
    private http: HttpClient,
    public authService: AuthService,
    private translateService: TranslateService,
    private userProfileClient: UserProfileClient
  ) { }

  ngOnInit() {
    if (!this.authService.isUserAdmin()) {
      this.loadCurrentUserLocation();
      this.loadDashboardData();
      this.loadTrustData();
      this.initChartOptions();
    }
  }

  private async loadCurrentUserLocation() {
    try {
      const userInfo = this.authService.getUserInfo();

      if (userInfo && userInfo.userId) {
        const userProfile = await firstValueFrom(
          this.userProfileClient.get()
        ) as UserProfileDTO;

        if (userProfile?.latitude && userProfile?.longitude) {
          this.currentUser = userProfile;

          this.createUserLocationMarker();

          this.mapCenter = {
            lat: this.currentUser.latitude,
            lng: this.currentUser.longitude,

          };
          this.mapZoom = 13;

        }
      }
    } catch (error) {
      console.error('Error loading user location:', error);
    }
  }


  private createUserLocationMarker() {
    const userMarkerIcon = this.createPinIcon(
      this.MARKER_COLORS.USER_LOCATION,
      1.0
    );

    const userName =
      `${this.currentUser!.firstName || ''} ${this.currentUser!.lastName || ''}`.trim() ||
      'Your Location';
    const userAddress = this.currentUser!.address || 'No address';

    this.userMarker = {
      position: {
        lat: this.currentUser!.latitude!,
        lng: this.currentUser!.longitude!
      },
      title: `${userName}\n${userAddress}\n(Your Location)`,
      label: {
        text: 'YOU',
        color: '#FFFFFF',
        fontSize: '10px',
        fontWeight: 'bold'
      } as any,
      icon: userMarkerIcon,
      isPendingOrder: false,
      isUserLocation: true,
      userId: this.currentUser!.userId!,
      zIndex: 1000
    };
  }


  loadDashboardData() {
    this.orderService.getSellerOrders().subscribe({
      next: async (orders: SellerOrderSummaryDTO[]) => {
        this.totalOrdersCount = orders.length;
        this.pendingOrdersCount = orders.filter(o => o.status === OrderStatus.Pending).length;

        await this.createMapMarkersFromOrders(orders);
        this.selectedMapFilter = 'PENDING';

        this.applyMapFilter();

        const statusLabels = [
          'Total Orders',
          'Pending',
          'Processing',
          'Out for Delivery',
          'Delivered',
          'Cancelled'
        ];

        const statusMap: Record<string, string> = {
          [OrderStatus.Pending]: 'Pending',
          [OrderStatus.Processing]: 'Processing',
          [OrderStatus.OutForDelivery]: 'Out for Delivery',
          [OrderStatus.Delivered]: 'Delivered',
          [OrderStatus.Cancelled]: 'Cancelled'
        };

        const statusGroups: Record<string, number> = {
          Pending: 0,
          Processing: 0,
          'Out for Delivery': 0,
          Delivered: 0,
          Cancelled: 0
        };

        orders.forEach(order => {
          const readableStatus = statusMap[order.status];
          if (readableStatus && statusGroups[readableStatus] !== undefined) {
            statusGroups[readableStatus]++;
          }
        });

        const statusData = [
          orders.length,
          statusGroups['Pending'],
          statusGroups['Processing'],
          statusGroups['Out for Delivery'],
          statusGroups['Delivered'],
          statusGroups['Cancelled']
        ];

        this.chartData = {
          labels: statusLabels,
          datasets: [
            {
              label: 'Orders',
              backgroundColor: [
                '#42A5F5',
                '#FFA726',
                '#26C6DA',
                '#7E57C2',
                '#66BB6A',
                '#EF5350'
              ],
              data: statusData
            }
          ]
        };
      },
      error: (err) => console.error('Error loading orders:', err)
    });
  }

  private async createMapMarkersFromOrders(orders: SellerOrderSummaryDTO[]) {
    this.orderMarkers = [];
    console.log('Total orders received:', orders.length);

    const buyerIds = [...new Set(orders.map(o => o.buyerId).filter(id => id))];
    console.log('Unique buyer IDs:', buyerIds);

    if (buyerIds.length === 0) {
      console.warn('No buyer IDs found in orders');
      return;
    }

    try {
      const buyerDetailsPromises = buyerIds.map(id =>
        this.fetchApplicationUserDetails(id!).catch(err => {
          console.error(`Failed to fetch buyer ${id}:`, err);
          return null;
        })
      );

      const buyerDetails = await Promise.all(buyerDetailsPromises);
      console.log('Fetched buyer details:', buyerDetails);

      const buyerMap = new Map<string, UserProfileDTO>(
        buyerDetails
          .filter((buyer): buyer is UserProfileDTO => {
            const isValid = buyer !== null &&
              buyer.latitude !== undefined &&
              buyer.latitude !== null &&
              buyer.longitude !== undefined &&
              buyer.longitude !== null;

            if (!isValid && buyer) {
              console.warn("Buyer missing coordinates:", buyer.userId, buyer.firstname, buyer.lastname);
            }

            return isValid;
          })
          .map(buyer => [buyer.userId!, buyer])
      );

      console.log("Buyers with valid coordinates:", buyerMap.size);

      const locationOrdersMap = new Map<string, {
        buyer: UserProfileDTO;
        orders: SellerOrderSummaryDTO[];
        mostRecentDate: DateTime;
        pendingOrders: SellerOrderSummaryDTO[];
      }>();

      orders.forEach(order => {
        const buyer = buyerMap.get(order.buyerId!);
        if (buyer && buyer.latitude && buyer.longitude) {
          const key = `${buyer.latitude},${buyer.longitude}`;
          const orderDate = order.orderDate || DateTime.now();

          console.log('Processing order:', order.orderNumber, 'Date:', orderDate.toFormat('dd MMM yyyy'));

          if (locationOrdersMap.has(key)) {
            const existing = locationOrdersMap.get(key)!;
            existing.orders.push(order);

            if (order.status === OrderStatus.Pending) {
              existing.pendingOrders.push(order);
            }

            if (orderDate > existing.mostRecentDate) {
              existing.mostRecentDate = orderDate;
            }
          } else {
            locationOrdersMap.set(key, {
              buyer,
              orders: [order],
              mostRecentDate: orderDate,
              pendingOrders: order.status === OrderStatus.Pending ? [order] : []
            });
          }
        } else {
          console.warn("Order skipped - buyer not found or missing coordinates:", order.buyerId);
        }
      });

      console.log('Location groups created:', locationOrdersMap.size);

      const now = DateTime.now();

      locationOrdersMap.forEach(({ buyer, orders: locationOrders, mostRecentDate, pendingOrders }) => {
        const daysSinceOrder = now.diff(mostRecentDate, 'days').days;

        let markerColor: string;
        let recencyLabel: string;

        if (daysSinceOrder <= 7) {
          markerColor = this.MARKER_COLORS.RECENT;
          recencyLabel = 'Recent (Last 7 days)';
        } else if (daysSinceOrder <= 30) {
          markerColor = this.MARKER_COLORS.MODERATE;
          recencyLabel = 'Moderate (7-30 days)';
        } else {
          markerColor = this.MARKER_COLORS.OLD;
          recencyLabel = 'Old (30+ days)';
        }

        const orderCount = locationOrders.length;
        const buyerName = `${buyer.firstName || ''} ${buyer.lastName || ''}`.trim() || 'Unknown';
        const address = buyer.address || 'No address';


        const markerIcon = this.createPinIcon(
          markerColor,
          orderCount > 1 ? 0.9 : 0.85
        );



        this.orderMarkers.push({
          position: {
            lat: buyer.latitude!,
            lng: buyer.longitude!
          },
          title: `${buyerName}\n${address}\nOrders: ${orderCount}\nPending: ${pendingOrders.length}\n${recencyLabel}\nLast order: ${mostRecentDate.toFormat('dd MMM yyyy')}`,
          label: orderCount > 1 ? {
            text: orderCount.toString(),
            color: '#FFFFFF',
            fontSize: '12px',
            fontWeight: 'bold'
          } as any : undefined,
          icon: markerIcon,
          orderDate: mostRecentDate,
          orderCount,
          isPendingOrder: false,
          userId: buyer.userId,
          zIndex: 500
        });


        pendingOrders.forEach((pendingOrder) => {

          const pendingMarkerIcon = this.createPinIcon(
            this.MARKER_COLORS.PENDING,
            1.3
          );


          this.orderMarkers.push({
            position: {
              lat: buyer.latitude!,
              lng: buyer.longitude!
            },
            title: `PENDING: ${buyerName}\n${pendingOrder.deliveryAddress || address}\nOrder: ${pendingOrder.orderNumber}`,
            icon: pendingMarkerIcon,
            orderDate: pendingOrder.orderDate || DateTime.now(),
            isPendingOrder: true,
            orderId: pendingOrder.id,
            userId: buyer.userId,
            zIndex: 900
          });
        });

        console.log('Marker created:', buyerName, 'at', buyer.latitude, buyer.longitude, 'Orders:', orderCount, 'Pending:', pendingOrders.length);
      });

      console.log("Total markers created:", this.orderMarkers.length);


      this.orderMarkers.sort((a, b) => {
        if (!a.orderDate || !b.orderDate) return 0;
        return b.orderDate.toMillis() - a.orderDate.toMillis();
      });

      this.allMarkers = [...this.orderMarkers];
      if (this.userMarker) {
        this.allMarkers.push(this.userMarker);
      }

      console.log("All markers (including user):", this.allMarkers.length);
    } catch (error) {
      console.error("Error creating map markers:", error);
    }
  }

  private async fetchApplicationUserDetails(userId: string): Promise<UserProfileDTO | null> {
    try {
      console.log(`Fetching user with ID: ${userId}`);

      const userProfile = await firstValueFrom(
        this.userProfileClient.getById(userId)
      ) as UserProfileDTO;

      if (userProfile) {
        console.log("Successfully fetched user:", userProfile);
        return userProfile;
      }
      return null;
    } catch (error: any) {
      console.error(`Failed to fetch user ${userId}:`, {
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        url: error.url
      });
      return null;
    }
  }

  private centerMapOnMarkersAndUser() {
    const allPositions = [...this.markers];

    if (allPositions.length === 0) return;

    if (allPositions.length === 1) {
      this.mapCenter = allPositions[0].position;
      this.mapZoom = 14;
      return;
    }

    let minLat = allPositions[0].position.lat;
    let maxLat = allPositions[0].position.lat;
    let minLng = allPositions[0].position.lng;
    let maxLng = allPositions[0].position.lng;

    allPositions.forEach(pos => {
      minLat = Math.min(minLat, pos.position.lat);
      maxLat = Math.max(maxLat, pos.position.lat);
      minLng = Math.min(minLng, pos.position.lng);
      maxLng = Math.max(maxLng, pos.position.lng);
    });

    this.mapCenter = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    };

    const latDiff = Math.abs(maxLat - minLat);
    const lngDiff = Math.abs(maxLng - minLng);
    const maxDiff = Math.max(latDiff, lngDiff);

    if (maxDiff < 0.01) this.mapZoom = 15;
    else if (maxDiff < 0.05) this.mapZoom = 13;
    else if (maxDiff < 0.1) this.mapZoom = 12;
    else if (maxDiff < 0.5) this.mapZoom = 10;
    else this.mapZoom = 8;
  }

  loadTrustData() {

    this.isLoadingTrustConnections = true;
    this.trustClient.getTrustedUsers().subscribe({
      next: async (trustRelationships: TrustRelationshipDTO[]) => {
        this.totalTrustedConnections = trustRelationships.length;

        const thirtyDaysAgo = DateTime.now().minus({ days: 30 });
        this.totalTrustedConnections = trustRelationships.length;
        this.newTrustedConnections = trustRelationships.filter(trust =>
          trust.createdOn &&
          trust.createdOn.isValid &&
          trust.createdOn >= thirtyDaysAgo
        ).length;

        // Load trust connections for display in cards
        await this.loadTrustConnectionCards(trustRelationships);
        this.isLoadingTrustConnections = false;
      },
      error: (err) => {
        console.error("Error loading trust relationships:", err);
        this.isLoadingTrustConnections = false;
      }
    });
  }

  private async loadTrustConnectionCards(trustRelationships: TrustRelationshipDTO[]) {
    this.trustConnections = [];
    console.log('Loading trust connection cards:', trustRelationships.length);

    const thirtyDaysAgo = DateTime.now().minus({ days: 30 });

    try {
      const trustConnectionPromises = trustRelationships.map(async (trust) => {
        if (!trust.trustedUserId) return null;

        try {
          const userProfile = await firstValueFrom(
            this.userProfileClient.getById(trust.trustedUserId)
          );

          if (userProfile) {
            const userName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || 'Unknown User';
            const userAddress = userProfile.address || 'No address provided';
            const connectedDate = trust.createdOn || DateTime.now();
            const isRecent = connectedDate >= thirtyDaysAgo;

            return {
              id: trust.id,
              userId: trust.trustedUserId,
              userName: userName,
              userAddress: userAddress,
              userPhotoBase64: userProfile.photoUrl,
              latitude: userProfile.latitude,
              longitude: userProfile.longitude,
              connectedDate: connectedDate,
              isRecent: isRecent
            } as TrustConnectionCard;
          }
        } catch (error) {
          console.error(`Error fetching user ${trust.trustedUserId}:`, error);
        }

        return null;
      });

      const results = await Promise.all(trustConnectionPromises);
      this.trustConnections = results.filter((conn): conn is TrustConnectionCard => conn !== null);

      // Sort by connection date (most recent first)
      this.trustConnections.sort((a, b) =>
        b.connectedDate.toMillis() - a.connectedDate.toMillis()
      );

      console.log('Trust connection cards loaded:', this.trustConnections.length);
    } catch (error) {
      console.error('Error loading trust connection cards:', error);
    }
  }

  applyMapFilter() {
    let filteredMarkers: MarkerInfo[] = [];


    const filterValue = typeof this.selectedMapFilter === 'string'
      ? this.selectedMapFilter
      : this.selectedMapFilter?.value || 'MY_LOCATION';

    console.log("Applying filter:", filterValue);
    console.log("Order markers available:", this.orderMarkers.length);
    console.log("User marker available:", this.userMarker ? "Yes" : "No");

    if (filterValue === "MY_LOCATION") {

      if (this.userMarker) {
        filteredMarkers = [this.userMarker];
        console.log("User location marker shown");
      }
    } else if (filterValue === "ALL") {

      filteredMarkers = this.orderMarkers.map(m => ({ ...m }));
      console.log("All order markers shown:", filteredMarkers.length);
    } else if (filterValue === "PENDING") {

      filteredMarkers = this.orderMarkers.filter(marker => marker.isPendingOrder === true);
      console.log('Pending markers filtered:', filteredMarkers.length);
    } else if (filterValue === 'RECENT_7') {

      filteredMarkers = this.orderMarkers.filter(marker => {
        if (!marker.orderDate) return false;
        const days = DateTime.now().diff(marker.orderDate, "days").days;
        return days <= 7;
      });
      console.log("Recent 7 days markers filtered:", filteredMarkers.length);
    }

    this.markers = filteredMarkers;
    this.centerMapOnMarkersAndUser();
  }

  private createPinIcon(color: string, scale: number = 1): google.maps.Icon {
    return {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 24 32">
          <path fill="${color}" d="M12 0C6.48 0 2 4.48 2 10c0 7.5 10 22 10 22s10-14.5 10-22C22 4.48 17.52 0 12 0z"/>
          <circle cx="12" cy="10" r="4" fill="white"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(32 * scale, 42 * scale),
      anchor: new google.maps.Point(16 * scale, 42 * scale)

    };
  }


  private initChartOptions() {
    this.chartOptions = {
      plugins: { legend: { position: "bottom" } },
      responsive: true,
      maintainAspectRatio: false
    };
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  formatConnectionDate(date: DateTime): string {
    const now = DateTime.now();
    const days = now.diff(date, 'days').days;

    if (days < 1) {
      return 'Today';
    } else if (days < 2) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${Math.floor(days)} days ago`;
    } else if (days < 30) {
      const weeks = Math.floor(days / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      return date.toFormat('dd MMM yyyy');
    }
  }

  onViewProfile(connection: TrustConnectionCard) {
    console.log('View profile:', connection);
    // Navigate to user profile or open modal
  }

  onRemoveTrust(connection: TrustConnectionCard) {
    console.log('Remove trust:', connection);
    // Implement remove trust functionality
    this.trustClient.removeTrust(connection.userId).subscribe({
      next: () => {
        this.trustConnections = this.trustConnections.filter(c => c.id !== connection.id);
        this.totalTrustedConnections--;
        console.log('Trust connection removed successfully');
      },
      error: (err) => console.error('Error removing trust:', err)
    });
  }

  onShowOnMap(connection: TrustConnectionCard) {
    if (connection.latitude && connection.longitude) {
      this.mapCenter = {
        lat: connection.latitude,
        lng: connection.longitude
      };
      this.mapZoom = 15;
      console.log('Centered map on:', connection.userName);
    }
  }
}